"use strict";

exports.__esModule = true;
exports.SkeletonData = void 0;

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores the setup pose and all of the stateless data for a skeleton.
 *
 * See [Data objects](http://esotericsoftware.com/spine-runtime-architecture#Data-objects) in the Spine Runtimes
 * Guide. */
var SkeletonData = /*#__PURE__*/function () {
  function SkeletonData() {
    /** The skeleton's name, which by default is the name of the skeleton data file, if possible. May be null. */
    this.name = null;
    /** The skeleton's bones, sorted parent first. The root bone is always the first bone. */

    this.bones = new Array(); // Ordered parents first.

    /** The skeleton's slots. */

    this.slots = new Array(); // Setup pose draw order.

    this.skins = new Array();
    /** The skeleton's default skin. By default this skin contains all attachments that were not in a skin in Spine.
     *
     * See {@link Skeleton#getAttachmentByName()}.
     * May be null. */

    this.defaultSkin = null;
    /** The skeleton's events. */

    this.events = new Array();
    /** The skeleton's animations. */

    this.animations = new Array();
    /** The skeleton's IK constraints. */

    this.ikConstraints = new Array();
    /** The skeleton's transform constraints. */

    this.transformConstraints = new Array();
    /** The skeleton's path constraints. */

    this.pathConstraints = new Array();
    /** The X coordinate of the skeleton's axis aligned bounding box in the setup pose. */

    this.x = 0;
    /** The Y coordinate of the skeleton's axis aligned bounding box in the setup pose. */

    this.y = 0;
    /** The width of the skeleton's axis aligned bounding box in the setup pose. */

    this.width = 0;
    /** The height of the skeleton's axis aligned bounding box in the setup pose. */

    this.height = 0;
    /** The Spine version used to export the skeleton data, or null. */

    this.version = null;
    /** The skeleton data hash. This value will change if any of the skeleton data has changed. May be null. */

    this.hash = null; // Nonessential

    /** The dopesheet FPS in Spine. Available only when nonessential data was exported. */

    this.fps = 0;
    /** The path to the images directory as defined in Spine. Available only when nonessential data was exported. May be null. */

    this.imagesPath = null;
    /** The path to the audio directory as defined in Spine. Available only when nonessential data was exported. May be null. */

    this.audioPath = null;
  }
  /** Finds a bone by comparing each bone's name. It is more efficient to cache the results of this method than to call it
   * multiple times.
   * @returns May be null. */


  var _proto = SkeletonData.prototype;

  _proto.findBone = function findBone(boneName) {
    if (!boneName) throw new Error("boneName cannot be null.");
    var bones = this.bones;

    for (var i = 0, n = bones.length; i < n; i++) {
      var bone = bones[i];
      if (bone.name == boneName) return bone;
    }

    return null;
  }
  /** Finds a slot by comparing each slot's name. It is more efficient to cache the results of this method than to call it
   * multiple times.
   * @returns May be null. */
  ;

  _proto.findSlot = function findSlot(slotName) {
    if (!slotName) throw new Error("slotName cannot be null.");
    var slots = this.slots;

    for (var i = 0, n = slots.length; i < n; i++) {
      var slot = slots[i];
      if (slot.name == slotName) return slot;
    }

    return null;
  }
  /** Finds a skin by comparing each skin's name. It is more efficient to cache the results of this method than to call it
   * multiple times.
   * @returns May be null. */
  ;

  _proto.findSkin = function findSkin(skinName) {
    if (!skinName) throw new Error("skinName cannot be null.");
    var skins = this.skins;

    for (var i = 0, n = skins.length; i < n; i++) {
      var skin = skins[i];
      if (skin.name == skinName) return skin;
    }

    return null;
  }
  /** Finds an event by comparing each events's name. It is more efficient to cache the results of this method than to call it
   * multiple times.
   * @returns May be null. */
  ;

  _proto.findEvent = function findEvent(eventDataName) {
    if (!eventDataName) throw new Error("eventDataName cannot be null.");
    var events = this.events;

    for (var i = 0, n = events.length; i < n; i++) {
      var event = events[i];
      if (event.name == eventDataName) return event;
    }

    return null;
  }
  /** Finds an animation by comparing each animation's name. It is more efficient to cache the results of this method than to
   * call it multiple times.
   * @returns May be null. */
  ;

  _proto.findAnimation = function findAnimation(animationName) {
    if (!animationName) throw new Error("animationName cannot be null.");
    var animations = this.animations;

    for (var i = 0, n = animations.length; i < n; i++) {
      var animation = animations[i];
      if (animation.name == animationName) return animation;
    }

    return null;
  }
  /** Finds an IK constraint by comparing each IK constraint's name. It is more efficient to cache the results of this method
   * than to call it multiple times.
   * @return May be null. */
  ;

  _proto.findIkConstraint = function findIkConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var ikConstraints = this.ikConstraints;

    for (var i = 0, n = ikConstraints.length; i < n; i++) {
      var constraint = ikConstraints[i];
      if (constraint.name == constraintName) return constraint;
    }

    return null;
  }
  /** Finds a transform constraint by comparing each transform constraint's name. It is more efficient to cache the results of
   * this method than to call it multiple times.
   * @return May be null. */
  ;

  _proto.findTransformConstraint = function findTransformConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var transformConstraints = this.transformConstraints;

    for (var i = 0, n = transformConstraints.length; i < n; i++) {
      var constraint = transformConstraints[i];
      if (constraint.name == constraintName) return constraint;
    }

    return null;
  }
  /** Finds a path constraint by comparing each path constraint's name. It is more efficient to cache the results of this method
   * than to call it multiple times.
   * @return May be null. */
  ;

  _proto.findPathConstraint = function findPathConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var pathConstraints = this.pathConstraints;

    for (var i = 0, n = pathConstraints.length; i < n; i++) {
      var constraint = pathConstraints[i];
      if (constraint.name == constraintName) return constraint;
    }

    return null;
  };

  return SkeletonData;
}();

exports.SkeletonData = SkeletonData;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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